import React, { ChangeEvent, Component } from "react";
import * as yaml from "js-yaml";

import { defined } from "@formant/core/common/defined";
import { IView } from "@formant/core/model/IView";
import {
    Button,
    Copy,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    Ellipsis,
    FileDown,
    Import,
    Trash,
} from "@formant/ui-kit";

import { viewClient } from "../../../clients";
import { appStore, authStore, routerStore, viewStore } from "../../../stores";
import { downloadFile } from "../../../utils/downloadFile";
import { Buttons } from "../../common/Buttons";
import { OverviewViewName } from "../../Overview/OverviewV2/OverviewV2";
import * as styles from "./Actions.scss";

export interface IActionsProps {
    view: IView;
    views: IView[];
    index: number;
}

export class Actions extends Component<IActionsProps> {
    fileRef: React.RefObject<HTMLInputElement> = React.createRef();

    public render(): React.ReactNode {
        const { onClone, onDelete } = this;

        const actions = [
            {
                icon: <Trash className="mr-2" />,
                label: "Remove",
                onSelect: onDelete,
            },
            {
                icon: <Copy className="mr-2" />,
                label: "Clone",
                onSelect: onClone,
            },
            {
                icon: <FileDown className="mr-2" />,
                label: "Export",
                onSelect: this.onExport,
            },
            {
                icon: <Import className="mr-2" />,
                label: "Import",
                onSelect: this.onImport,
            },
        ];

        return (
            <Buttons>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild={true}>
                        <Button
                            variant="tertiary"
                            size="icon"
                            className="[&_svg]:size-6 bg-formant-color-background-secondary hover:bg-formant-color-background-active-2"
                        >
                            <Ellipsis className="text-formant-color-text-secondary" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                        {actions.map((action, idx) => (
                            <DropdownMenuItem
                                key={action.label || idx}
                                onClick={() => setTimeout(action.onSelect, 0)}
                            >
                                {action.icon}
                                {action.label}
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
                <input
                    type="file"
                    className={styles["import-file"]}
                    ref={this.fileRef}
                    onChange={this.onImportFileSelected}
                    onClick={this.onImportFileClicked}
                />
            </Buttons>
        );
    }

    private onClone = async () => {
        const { navigate, routeTo } = routerStore;
        await navigate(routeTo.cloneView({ id: defined(this.props.view.id) }));
    };

    private onDelete = () => {
        const { view } = this.props;
        const { name, id } = view;
        if (name === OverviewViewName) {
            appStore.setSnackbar("Cannot delete overview view");
            return;
        }
        appStore.setConfirm({
            title: "Delete view",
            message: `Delete view "${name}"?`,
            onOk: async () => {
                await viewStore.delete(defined(id));
                appStore.setSnackbar("View deleted");
            },
        });
    };

    private onImport = () => {
        if (this.fileRef && this.fileRef.current) {
            this.fileRef.current.click();
        }
    };

    private onImportFileClicked(
        e: React.MouseEvent<HTMLInputElement, MouseEvent>,
    ) {
        e.stopPropagation();
    }

    private onImportFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
        const { view } = this.props;

        const fileEl = event.target;
        if (!fileEl.files) {
            return;
        }

        const fr = new FileReader();
        fr.onload = async () => {
            if (!view.id) {
                return;
            }
            const newView = await viewClient.get(
                await authStore.token(),
                view.id,
            );
            if (!newView) {
                return;
            }
            if (typeof fr.result === "string") {
                try {
                    const configuration = yaml.load(fr.result) as
                        | IView
                        | null
                        | undefined;
                    if (configuration?.filter) {
                        newView.filter = configuration.filter;
                    }
                    if (configuration?.layout) {
                        newView.layout = configuration.layout;
                    }
                    if (configuration?.configuration) {
                        newView.configuration = configuration.configuration;
                    }
                    await viewClient.update(
                        await authStore.token(),
                        defined(newView.id),
                        newView,
                    );
                    appStore.setSnackbar("View imported");
                } catch (e) {
                    appStore.setSnackbar("Failed to import view");
                }
            }
        };

        fr.readAsText(fileEl.files[0]);
    };

    private onExport = async () => {
        const { view } = this.props;

        if (!view.id) {
            return;
        }

        const configuration: any = await viewClient.get(
            await authStore.token(),
            view.id,
        );
        if (!configuration) {
            return;
        }

        delete configuration.id;
        delete configuration.name;
        delete configuration.createdAt;
        delete configuration.updatedAt;
        delete configuration.organizationId;
        delete configuration.index;
        delete configuration.tags;
        downloadFile(view.name + ".yaml", "text/yaml", configuration);
    };
}
